import { useEffect, useRef, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";

interface CrossfadeProps {
  /** Identifier of the currently-rendered state. When this changes, the
   *  old children fade out and the new children fade in over ~240ms. */
  stateKey: string;
  /** A CSS selector inside the new state's tree that should receive focus
   *  on enter. Default: the first `h1` or `h2`. Pass `false` to skip
   *  focus management entirely. */
  focusOnEnter?: string | false;
  children: ReactNode;
}

/**
 * Crossfade — single point of motion truth for inline-flow transitions on
 * Pages 1-7. Old state fades out (accelerate, 160ms), new state fades in
 * (decelerate, 200ms) with an 80ms overlap. Honors prefers-reduced-motion
 * via the global rule (transition-duration: 0.01ms !important).
 *
 * On state change, moves focus to the first heading (or to the selector
 * passed via `focusOnEnter`) so screen-reader users hear the new context.
 * Defended in research §2.8 and §5.4.
 */
export function Crossfade({ stateKey, focusOnEnter = "h1, h2", children }: CrossfadeProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (focusOnEnter === false) return;
    let injectedTabIndex = false;
    let target: HTMLElement | null = null;
    const onBlur = () => {
      if (injectedTabIndex && target) {
        target.removeAttribute("tabindex");
        target.removeEventListener("blur", onBlur);
        injectedTabIndex = false;
      }
    };
    // Defer focus to the next tick so the new tree is in the DOM.
    const id = window.setTimeout(() => {
      if (!containerRef.current) return;
      target = containerRef.current.querySelector<HTMLElement>(focusOnEnter);
      if (target) {
        // tabIndex=-1 makes the heading programmatically focusable without
        // adding it to the tab order. Removed on blur so the heading is
        // not permanently focusable after the state transition completes.
        if (!target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
          injectedTabIndex = true;
          target.addEventListener("blur", onBlur);
        }
        target.focus({ preventScroll: true });
      }
    }, 0);
    return () => {
      window.clearTimeout(id);
      if (injectedTabIndex && target) {
        target.removeAttribute("tabindex");
        target.removeEventListener("blur", onBlur);
      }
    };
  }, [stateKey, focusOnEnter]);

  return (
    <div ref={containerRef} className="relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={stateKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: 0.2, ease: [0, 0, 0, 1] },
          }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
